import React, { useState, useEffect, useCallback } from 'react';
import { getUpdateList, updateRack } from '../api';

const pgBtnStyle = (active, disabled) => ({
  minWidth: 28, height: 28, padding: '0 7px',
  borderRadius: 6, border: active ? 'none' : '1px solid #dee2e6',
  background: active ? 'linear-gradient(135deg,#4f46e5,#6366f1)' : disabled ? '#f8f9fa' : '#fff',
  color: active ? '#fff' : disabled ? '#adb5bd' : '#495057',
  fontWeight: active ? 700 : 500, fontSize: '0.78rem',
  cursor: disabled ? 'not-allowed' : 'pointer',
  boxShadow: active ? '0 2px 6px rgba(99,102,241,0.3)' : 'none',
  transition: 'all 0.15s',
});

export default function UpdateServerDetails() {
  const [allRows, setAllRows]         = useState([]);
  const [displayRows, setDisplayRows] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [statusMsg, setStatusMsg]     = useState('');
  const [statusType, setStatusType]   = useState('success');
  const [saving, setSaving]           = useState(false);
  const [searchIP, setSearchIP]       = useState('');
  const [rowState, setRowState]       = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize]       = useState(15);

  const fetchData = useCallback(() => {
    setLoading(true); setError(''); setStatusMsg(''); setRowState(''); setSearchIP('');
    getUpdateList()
      .then(r => {
        const data = r.data || [];
        setAllRows(data); setDisplayRows(data);
        const initial = {};
        data.forEach((row, i) => { initial[i] = { checked: false, rack: row.RACK || '' }; });
        setRowState(initial);
      })
      .catch(e => setError(e?.response?.data?.message || 'Failed to load data.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const applySearch = (ip) => {
    const term = (ip || '').trim().toLowerCase();
    setDisplayRows(term ? allRows.filter(r => (r.SERVERIP || '').toLowerCase().includes(term)) : allRows);
    setCurrentPage(1);
  };

  const handleGo    = () => applySearch(searchIP);
  const handleReset = () => { setSearchIP(''); setDisplayRows(allRows); setStatusMsg(''); setCurrentPage(1); };

  const getRowKey       = (row) => allRows.indexOf(row);
  const toggleCheck     = (key) => setRowState(prev => ({ ...prev, [key]: { ...prev[key], checked: !prev[key]?.checked } }));
  const updateRackValue = (key, val) => setRowState(prev => ({ ...prev, [key]: { ...prev[key], rack: val } }));

  const allDisplayedChecked = displayRows.length > 0 && displayRows.every(row => rowState[getRowKey(row)]?.checked);
  const toggleAll = () => {
    const newChecked = !allDisplayedChecked;
    setRowState(prev => {
      const next = { ...prev };
      displayRows.forEach(row => { const k = getRowKey(row); next[k] = { ...next[k], checked: newChecked }; });
      return next;
    });
  };

  const selectedCount = Object.values(rowState).filter(s => s.checked).length;

  const handleUpdateSelected = () => {
    setStatusMsg('');
    const payload = allRows
      .map((row, i) => ({ row, state: rowState[i] }))
      .filter(({ state }) => state?.checked)
      .map(({ row, state }) => ({ SERVERIP: row.SERVERIP, RACK: state.rack }));
    if (payload.length === 0) { setStatusMsg('No rows selected.'); setStatusType('danger'); return; }
    setSaving(true);
    updateRack(payload)
      .then(() => { setStatusMsg(`${payload.length} record(s) updated successfully.`); setStatusType('success'); fetchData(); })
      .catch(e => { setStatusMsg(e?.response?.data?.message || 'Update failed.'); setStatusType('danger'); })
      .finally(() => setSaving(false));
  };

  const totalRows  = displayRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const pagedRows  = displayRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const pageNumbers = React.useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1].filter(p => p >= 1 && p <= totalPages));
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('…');
      result.push(sorted[i]);
    }
    return result;
  }, [totalPages, currentPage]);

  return (
    <div className="py-2">

      {/* ── Compact toolbar: heading + search + action all in one row ── */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <h5 className="fw-bold text-dark mb-0 me-2">
          <i className="bi bi-pencil-square me-2 text-primary" />Update Server Details
        </h5>

        {/* Search */}
        <div className="input-group input-group-sm" style={{ width: 200 }}>
          <span className="input-group-text bg-light border-end-0 text-secondary">
            <i className="bi bi-search" style={{ fontSize: '0.75rem' }} />
          </span>
          <input
            type="text"
            className="form-control border-start-0 ps-0"
            placeholder="Search IP..."
            value={searchIP}
            onChange={e => setSearchIP(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleGo()}
          />
        </div>
        <button className="btn btn-sm btn-outline-primary" onClick={handleGo}>Search</button>
        {searchIP && (
          <button className="btn btn-sm btn-light border text-secondary" onClick={handleReset}>
            <i className="bi bi-x" />
          </button>
        )}

        {/* Stats */}
        <span className="badge bg-light text-secondary border px-2 py-1 fw-medium ms-1" style={{ fontSize: '0.78rem' }}>
          {totalRows !== allRows.length
            ? <>{totalRows} <span className="text-muted">/ {allRows.length}</span></>
            : <>{totalRows} records</>}
        </span>

        {/* Update button */}
        <div className="ms-auto">
          <button
            className="btn btn-sm btn-primary shadow-sm"
            onClick={handleUpdateSelected}
            disabled={saving || selectedCount === 0}
          >
            {saving
              ? <><span className="spinner-border spinner-border-sm me-1" />Updating...</>
              : <><i className="bi bi-check2-circle me-1" />Update Selected {selectedCount > 0 && `(${selectedCount})`}</>}
          </button>
        </div>
      </div>

      {/* ── Messages ── */}
      {error     && <div className="alert alert-danger py-1 px-3 small fw-medium rounded-3 mb-2">{error}</div>}
      {statusMsg && <div className={`alert alert-${statusType} py-1 px-3 small fw-medium rounded-3 mb-2`}>{statusMsg}</div>}

      {/* ── Table ── */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
          <div className="mt-2 text-secondary small">Loading...</div>
        </div>
      ) : (
        <>
        <div className="border rounded shadow-sm" style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0, overflow: 'hidden' }}>
          <table className="table table-hover table-sm align-middle mb-0" style={{ fontSize: '0.82rem' }}>
            <thead className="sticky-top bg-white shadow-sm" style={{ zIndex: 11 }}>
              <tr className="text-secondary" style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                <th style={{ width: 36, padding: '6px 10px' }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allDisplayedChecked}
                    onChange={toggleAll}
                    disabled={displayRows.length === 0}
                    title="Select all visible rows"
                  />
                </th>
                <th style={{ padding: '6px 8px' }}>SERVER IP</th>
                <th style={{ padding: '6px 8px', minWidth: 180 }}>RACK</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.length === 0 ? (
                <tr><td colSpan={3} className="text-center text-muted py-4">No records found.</td></tr>
              ) : (
                pagedRows.map((row, idx) => {
                  const key   = getRowKey(row);
                  const state = rowState[key] || { checked: false, rack: row.RACK || '' };
                  return (
                    <tr
                      key={idx}
                      style={{ background: state.checked ? '#eef2ff' : undefined, cursor: 'default' }}
                      onClick={() => toggleCheck(key)}
                    >
                      <td style={{ padding: '4px 10px' }} onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={state.checked}
                          onChange={() => toggleCheck(key)}
                        />
                      </td>
                      <td style={{ padding: '4px 8px', fontFamily: 'monospace', color: '#374151' }}>
                        {row.SERVERIP || ''}
                      </td>
                      <td style={{ padding: '4px 8px' }} onClick={e => e.stopPropagation()}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={state.rack}
                          onChange={e => updateRackValue(key, e.target.value)}
                          style={{ height: 26, fontSize: '0.8rem', maxWidth: 200 }}
                          placeholder="Enter rack..."
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination bar ── */}
        {totalRows > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 8, padding: '8px 14px',
            background: '#f8f9ff', borderRadius: '0 0 8px 8px',
            border: '1px solid #dee2e6', borderTop: 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', color: '#6c757d' }}>
              <span>
                <strong style={{ color: '#212529' }}>{(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalRows)}</strong>
                {' '}of{' '}
                <strong style={{ color: '#212529' }}>{totalRows}</strong>
              </span>
              <span style={{ color: '#dee2e6' }}>|</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 0 }}>
                Rows
                <select
                  value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                  style={{ border: '1px solid #dee2e6', borderRadius: 5, padding: '1px 4px', fontSize: '0.78rem', background: '#fff', color: '#212529', cursor: 'pointer' }}
                >
                  {[10, 15, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={pgBtnStyle(false, currentPage === 1)}>
                <i className="bi bi-chevron-left" style={{ fontSize: '0.7rem' }} />
              </button>
              {pageNumbers.map((p, i) =>
                p === '…'
                  ? <span key={`e-${i}`} style={{ padding: '0 3px', color: '#adb5bd', fontSize: '0.8rem' }}>…</span>
                  : <button key={p} onClick={() => setCurrentPage(p)} style={pgBtnStyle(p === currentPage, false)}>{p}</button>
              )}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={pgBtnStyle(false, currentPage === totalPages)}>
                <i className="bi bi-chevron-right" style={{ fontSize: '0.7rem' }} />
              </button>
            </div>

            <div style={{ fontSize: '0.78rem', color: '#6c757d' }}>
              Page <strong style={{ color: '#212529' }}>{currentPage}</strong> / <strong style={{ color: '#212529' }}>{totalPages}</strong>
            </div>
          </div>
        )}
        </>
      )}
    </div>
  );
}
